import { useEffect, ReactNode } from "react"
import { X } from "lucide-react"
import { clsx } from "clsx"

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  icon?: string
  size?: "sm" | "md" | "lg"
  children: ReactNode
  footer?: ReactNode
}

export function Modal({ open, onClose, title, icon, size = "md", children, footer }: ModalProps) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, onClose])

  if (!open) return null

  const widths = { sm: "max-w-md", md: "max-w-lg", lg: "max-w-3xl" }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={clsx("w-full bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh]", widths[size])}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            {icon && <span>{icon}</span>}
            {title}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors rounded-lg p-1 hover:bg-gray-100" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0 bg-gray-50 rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// Form helpers used inside modals
export function FormGroup({ label, required, hint, children }: {
  label: string; required?: boolean; hint?: string; children: ReactNode
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  )
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={clsx(
        "w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900",
        "focus:outline-none focus:ring-2 focus:ring-zimrah-500 focus:border-transparent",
        "placeholder-gray-400 disabled:bg-gray-50 disabled:text-gray-500 transition-all",
        props.className
      )}
    />
  )
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={clsx(
        "w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900 bg-white",
        "focus:outline-none focus:ring-2 focus:ring-zimrah-500 focus:border-transparent transition-all",
        props.className
      )}
    />
  )
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      className={clsx(
        "w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-900",
        "focus:outline-none focus:ring-2 focus:ring-zimrah-500 focus:border-transparent",
        "placeholder-gray-400 resize-vertical min-h-[100px] transition-all",
        props.className
      )}
    />
  )
}